/**
 * Phase 36.5.B — Watchlist Snapshot Store (hardened iter31, iter84 localStorage).
 *
 * Module-scoped, cross-mount state for the canonical watchlist.
 *
 * Persistence tiers:
 *   1. In-memory (`_state`) — survives Dashboard mount/unmount.
 *   2. localStorage (`atde_wl_v1`) — survives full page reload AND
 *      cross-session (logout → re-login next day). This closes the
 *      "watchlist blank on cold-start" flash where the user briefly saw
 *      "—" on every row until /watchlist/snapshot resolved (3-6s).
 *      Iter 84 promoted this from sessionStorage → localStorage so
 *      re-authentication no longer wipes it.
 *
 * Why the persistence: the user-reported bug "newly added watchlist
 * symbols regress to '—' after tab switch / logout" happens because
 * (a) the JS context resets on a full reload (logout cycle), wiping the
 * module's quote cache, and (b) the backend `/watchlist/snapshot` row
 * for a fresh symbol takes one BG-scanner cycle before it carries a
 * real price. Persisting the in-memory map to localStorage and
 * eagerly hydrating on add closes both holes.
 *
 * Public API:
 *   store.subscribe(fn)                       -> unsubscribe
 *   store.getRows()                           -> last-known canonical rows
 *   store.getRow(symbol)                      -> single canonical row
 *   store.refresh()                           -> async, hits /api/watchlist/snapshot
 *   store.reconcileSSE(payload)               -> merge an SSE patch
 *   store.setQuote(sym, q) / .getQuote(sym)
 *   store.setSymbols([…]) / .getSymbols()
 *   store.removeSymbol(sym)                   -> drop from bySymbol & quotes
 *   store.clear()                             -> wipe (used on operator change)
 */
import api from '../lib/api';
import broadcast from '../lib/crossTabBroadcast';

const STORAGE_KEY = 'atde_wl_v1';

function _loadFromStorage() {
  // Iter 84 — read from localStorage first (survives sessions), fall
  // back to legacy sessionStorage key so users upgrading in-flight
  // don't lose their cached rows.
  try {
    if (typeof window === 'undefined') return null;
    const raw = (window.localStorage && window.localStorage.getItem(STORAGE_KEY))
              || (window.sessionStorage && window.sessionStorage.getItem(STORAGE_KEY));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object') return parsed;
  } catch {}
  return null;
}

const _persisted = _loadFromStorage() || {};

const _state = {
  rows:            Array.isArray(_persisted.rows) ? _persisted.rows : [],
  bySymbol:        (_persisted.bySymbol && typeof _persisted.bySymbol === 'object') ? _persisted.bySymbol : {},
  quotes:          (_persisted.quotes   && typeof _persisted.quotes   === 'object') ? _persisted.quotes   : {},
  symbols:         Array.isArray(_persisted.symbols) ? _persisted.symbols : [],
  snapshotVersion: Number(_persisted.snapshotVersion) || 0,
  lastFetchedAt:   null,
  fetching:        false,
  lastError:       null,
};

let _persistTimer = null;
function _persist() {
  if (typeof window === 'undefined' || !window.localStorage) return;
  if (_persistTimer) return;          // batch ~50ms
  _persistTimer = setTimeout(() => {
    _persistTimer = null;
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({
        rows:            _state.rows,
        bySymbol:        _state.bySymbol,
        quotes:          _state.quotes,
        symbols:         _state.symbols,
        snapshotVersion: _state.snapshotVersion,
      }));
    } catch {}
  }, 50);
}

const _listeners = new Set();
function emit() {
  _persist();
  for (const fn of _listeners) {
    try { fn(snapshot()); } catch {}
  }
}

function snapshot() {
  return {
    rows: _state.rows,
    bySymbol: _state.bySymbol,
    quotes: _state.quotes,
    symbols: _state.symbols,
    snapshotVersion: _state.snapshotVersion,
    lastFetchedAt: _state.lastFetchedAt,
    fetching: _state.fetching,
    lastError: _state.lastError,
  };
}

function mergeIncoming(rows) {
  // Phase 36.5.B — preserve last-known-good. Never overwrite a known
  // price with `null`. New-symbol stub rows (price=null,
  // snapshot_version=0) are still added so they appear in the list,
  // but any field they blank that we previously knew is preserved.
  const merged = [];
  const next = { ..._state.bySymbol };   // start from existing — symbols not in the new payload stay
  let maxVer = _state.snapshotVersion || 0;
  for (const r of rows || []) {
    if (!r || !r.symbol) continue;
    const sym = r.symbol;
    const prev = _state.bySymbol[sym];
    let row = { ...r };
    if (prev) {
      // Preserve previously hydrated fields when the new row blanks them.
      ['price', 'company_name', 'sector', 'exchange', 'decision',
       'confidence', 'target_price', 'stop', 'entry_zone', 'exit_risk'].forEach((k) => {
        if (row[k] == null && prev[k] != null) row[k] = prev[k];
      });
      if (!row.last_good_payload && prev.last_good_payload) {
        row.last_good_payload = prev.last_good_payload;
      }
    }
    next[sym] = row;
    const v = row.snapshot_version || 0;
    if (v > maxVer) maxVer = v;
  }
  // Build merged list using the canonical operator symbol order when known.
  const ordered = (_state.symbols && _state.symbols.length)
    ? _state.symbols.map((s) => next[s]).filter(Boolean)
    : (rows || []).map((r) => next[r.symbol]).filter(Boolean);
  // Append any straggler not in the symbol list (defensive).
  for (const sym of Object.keys(next)) {
    if (!ordered.find((r) => r.symbol === sym)) ordered.push(next[sym]);
  }
  _state.rows = ordered;
  _state.bySymbol = next;
  _state.snapshotVersion = maxVer;
}

async function refresh() {
  if (_state.fetching) return snapshot();
  _state.fetching = true;
  emit();
  try {
    const { data } = await api.get('/watchlist/snapshot');
    mergeIncoming(data?.rows || []);
    _state.lastFetchedAt = Date.now();
    _state.lastError = null;
  } catch (e) {
    _state.lastError = e?.message || 'refresh_failed';
  } finally {
    _state.fetching = false;
    emit();
  }
  return snapshot();
}

function reconcileSSE(payload) {
  if (!payload) return;
  if (Array.isArray(payload.rows)) {
    mergeIncoming(payload.rows);
    _state.lastFetchedAt = Date.now();
    emit();
    return;
  }
  if (payload.symbol && payload.row) {
    mergeIncoming([{ ...payload.row, symbol: payload.symbol }]);
    emit();
  }
}

function subscribe(fn) {
  if (typeof fn !== 'function') return () => {};
  _listeners.add(fn);
  try { fn(snapshot()); } catch {}
  return () => _listeners.delete(fn);
}

function getRows() { return _state.rows; }
function getRow(sym) { return _state.bySymbol[sym] || null; }

function setQuote(sym, q) {
  // Only persist a real positive quote — never wipe a known price with
  // a transient null/zero from a throttled batch response.
  if (!sym || !q) return;
  if (typeof q.c !== 'number' || q.c <= 0) return;
  _state.quotes = { ..._state.quotes, [sym]: { c: q.c, dp: q.dp ?? null, ts: Date.now() } };
  emit();
  // Phase 39 — broadcast to other tabs.
  try { broadcast.publish('watchlist', { kind: 'quote', symbol: sym, quote: _state.quotes[sym] }); } catch {}
}
function getQuote(sym) { return _state.quotes[sym] || null; }
function getQuotes() { return _state.quotes; }

function setSymbols(syms) {
  if (!Array.isArray(syms)) return;
  _state.symbols = syms.slice();
  emit();
  try { broadcast.publish('watchlist', { kind: 'symbols', symbols: _state.symbols }); } catch {}
}
function getSymbols() { return _state.symbols; }

function removeSymbol(sym) {
  if (!sym) return;
  if (_state.symbols.includes(sym)) {
    _state.symbols = _state.symbols.filter((s) => s !== sym);
  }
  if (_state.bySymbol[sym]) {
    const nb = { ..._state.bySymbol }; delete nb[sym]; _state.bySymbol = nb;
  }
  if (_state.quotes[sym]) {
    const nq = { ..._state.quotes }; delete nq[sym]; _state.quotes = nq;
  }
  _state.rows = _state.rows.filter((r) => r.symbol !== sym);
  emit();
  try { broadcast.publish('watchlist', { kind: 'remove', symbol: sym }); } catch {}
}

function clear() {
  _state.rows = [];
  _state.bySymbol = {};
  _state.quotes = {};
  _state.symbols = [];
  _state.snapshotVersion = 0;
  emit();
}

const store = {
  subscribe, refresh, reconcileSSE,
  getRows, getRow,
  getQuote, getQuotes, setQuote,
  getSymbols, setSymbols, removeSymbol,
  clear, snapshot,
};

// Phase 39 — subscribe to cross-tab updates from this tab's siblings.
// Other tabs' changes feed back into the in-memory state via the
// merge/setter functions (which re-broadcast — but the listener
// path goes through the same emit + sessionStorage persistence).
if (typeof window !== 'undefined' && typeof window.BroadcastChannel === 'function') {
  broadcast.subscribe('watchlist', (msg) => {
    if (!msg || typeof msg !== 'object') return;
    if (msg.kind === 'symbols' && Array.isArray(msg.symbols)) {
      // direct write — DO NOT call setSymbols (would re-broadcast).
      _state.symbols = msg.symbols.slice();
      emit();
    } else if (msg.kind === 'quote' && msg.symbol && msg.quote) {
      _state.quotes = { ..._state.quotes, [msg.symbol]: msg.quote };
      emit();
    } else if (msg.kind === 'remove' && msg.symbol) {
      _state.symbols = _state.symbols.filter((s) => s !== msg.symbol);
      const nb = { ..._state.bySymbol }; delete nb[msg.symbol]; _state.bySymbol = nb;
      const nq = { ..._state.quotes }; delete nq[msg.symbol]; _state.quotes = nq;
      _state.rows = _state.rows.filter((r) => r.symbol !== msg.symbol);
      emit();
    }
  });
}

export default store;
